import { FormControl, FormGroup, Validators } from '@angular/forms';

// How a work item line is priced: computed (parts + labor) or a flat-fee override.
export type PricingMode = 'computed' | 'flat';

/**
 * The form controls shared by the work-item edit modal and the create-quote
 * wizard. Hosts spread these into their own FormGroup alongside host-specific
 * controls (invoiceVehicleId / clearRejection in the modal, vehicleId in the
 * wizard).
 */
export function buildWorkItemFormControls(): { [key: string]: FormControl } {
  return {
    title: new FormControl('', [Validators.required]),
    empUserId: new FormControl(''),
    miles: new FormControl('', [Validators.pattern(/^\d*$/)]),
    shopHours: new FormControl('', [Validators.pattern(/^\d*$/)]),
    // Quarter-hour dropdown; no validators needed since the values are fixed.
    shopMinutes: new FormControl(''),
    partsPricing: new FormControl('', [Validators.pattern(/^\d*(\.\d{1,2})?$/)]),
    pricingMode: new FormControl<PricingMode>('computed'),
    flatFee: new FormControl('', [Validators.pattern(/^\d*(\.\d{1,2})?$/)]),
    flatFeeTaxable: new FormControl(false),
    notes: new FormControl('')
  };
}

/** Disable the inactive mode's inputs; values are retained, never destroyed. */
export function applyPricingMode(form: FormGroup, mode: PricingMode): void {
  const computedControls = ['shopHours', 'shopMinutes', 'partsPricing'];
  const flatControls = ['flatFee', 'flatFeeTaxable'];
  for (const name of mode === 'flat' ? flatControls : computedControls) {
    form.get(name)?.enable({ emitEvent: false });
  }
  for (const name of mode === 'flat' ? computedControls : flatControls) {
    form.get(name)?.disable({ emitEvent: false });
  }
}

export function isFlatMode(form: FormGroup): boolean {
  return form.get('pricingMode')?.value === 'flat';
}

export function parseMoney(value: unknown): number | undefined {
  if (value === '' || value == null) return undefined;
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Combine the hours input and quarter-hour dropdown into a decimal (1 hr 30 min => 1.5). */
export function composeShopHours(form: FormGroup): number | undefined {
  const hoursValue = form.get('shopHours')?.value;
  const minutesValue = form.get('shopMinutes')?.value;
  const hours = hoursValue !== '' && hoursValue !== null ? parseInt(hoursValue, 10) : 0;
  const minutes = minutesValue !== '' && minutesValue !== null ? parseInt(minutesValue, 10) : 0;
  if (hours === 0 && minutes === 0) {
    return undefined;
  }
  return Math.round((hours + minutes / 60) * 100) / 100;
}

// Split a decimal hour value into the hours input and the quarter-hour
// dropdown (e.g. 1.5 => 1 hr / "30").
export function decomposeShopHours(shopHours?: number): { hours: string; minutes: string } {
  if (shopHours == null) {
    return { hours: '', minutes: '' };
  }
  let hours = Math.floor(shopHours);
  let minutes = Math.round(((shopHours - hours) * 60) / 15) * 15;
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  return { hours: `${hours}`, minutes: minutes > 0 ? `${minutes}` : '' };
}
